import { useMemo } from "react";
import type { ScopedProjectRef } from "@cmmd-forge/contracts";

import { selectProjectByRef, selectSidebarThreadsForProjectRef, useStore } from "~/store";
import type { SidebarThreadSummary } from "~/types";
import type { ForgeProjectRemovalTarget } from "./CmmdProjectDeleteControl";
import type { ForgeExecutionTarget } from "./forgeExecutionTarget";
import type { ProjectWorkSessionsState } from "./useProjectWorkSessions";
import {
  projectRefsForRemoval,
  resolveForgeProjectRemovalRefs,
} from "./forgeProjectRemovalContext.logic";

interface ForgeProjectRemovalContext {
  readonly projectRefs: readonly ScopedProjectRef[];
  readonly removalTargets: readonly ForgeProjectRemovalTarget[];
  readonly threadSummaries: readonly SidebarThreadSummary[];
}

export function useForgeProjectRemovalContext(
  forgeTarget: ForgeExecutionTarget | null,
  allSessions: ProjectWorkSessionsState["allSessions"],
): ForgeProjectRemovalContext {
  const activeEnvironmentId = useStore((state) => state.activeEnvironmentId);
  const environmentStateById = useStore((state) => state.environmentStateById);
  const projectRefs = useMemo(
    () =>
      resolveForgeProjectRemovalRefs(
        environmentStateById,
        projectRefsForRemoval(forgeTarget, allSessions),
      ),
    [allSessions, environmentStateById, forgeTarget],
  );
  const removalTargets = useMemo(
    () =>
      projectRefs.flatMap((ref) => {
        const project = selectProjectByRef({ activeEnvironmentId, environmentStateById }, ref);
        return project
          ? [
              {
                environmentId: String(ref.environmentId),
                projectId: String(ref.projectId),
                cwd: project.cwd,
              },
            ]
          : [];
      }),
    [activeEnvironmentId, environmentStateById, projectRefs],
  );
  const threadSummaries = useMemo(() => {
    const byThreadId = new Map<string, SidebarThreadSummary>();
    for (const ref of projectRefs) {
      for (const thread of selectSidebarThreadsForProjectRef(
        { activeEnvironmentId, environmentStateById },
        ref,
      )) {
        byThreadId.set(thread.id, thread);
      }
    }
    return [...byThreadId.values()];
  }, [activeEnvironmentId, environmentStateById, projectRefs]);
  return { projectRefs, removalTargets, threadSummaries };
}
